import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { useLanguage, COMPANY, LOGO_URL } from "@/i18n";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export const Contact = () => {
  const { t } = useLanguage();
  const c = t.contact;

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div {...fadeUp} className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">{c.overline}</span>
          <h2
            className="mt-4 text-3xl sm:text-4xl md:text-5xl font-black tracking-tight"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {c.title}
          </h2>
          <p className="mt-5 text-lg text-slate-400 leading-relaxed">{c.subtitle}</p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 gap-6">
          <motion.a
            {...fadeUp}
            data-testid="contact-phone"
            href={`tel:${COMPANY.phoneHref}`}
            className="group flex items-start gap-5 rounded-2xl border border-white/10 bg-white/5 p-7 hover:bg-white/10 hover:border-white/20 transition-all"
          >
            <div className="h-12 w-12 rounded-xl bg-indigo-500/20 text-indigo-300 flex items-center justify-center shrink-0">
              <Phone className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{c.phoneLabel}</div>
              <div className="mt-1.5 text-xl font-semibold">{COMPANY.phone}</div>
            </div>
            <ArrowUpRight className="h-5 w-5 text-slate-500 group-hover:text-white transition-colors" />
          </motion.a>

          <motion.a
            {...fadeUp}
            data-testid="contact-email"
            href={`mailto:${COMPANY.email}`}
            className="group flex items-start gap-5 rounded-2xl border border-white/10 bg-white/5 p-7 hover:bg-white/10 hover:border-white/20 transition-all"
          >
            <div className="h-12 w-12 rounded-xl bg-indigo-500/20 text-indigo-300 flex items-center justify-center shrink-0">
              <Mail className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{c.emailLabel}</div>
              <div className="mt-1.5 text-xl font-semibold break-all lowercase">{COMPANY.email}</div>
            </div>
            <ArrowUpRight className="h-5 w-5 text-slate-500 group-hover:text-white transition-colors shrink-0" />
          </motion.a>

          <motion.div
            {...fadeUp}
            data-testid="contact-address"
            className="flex items-start gap-5 rounded-2xl border border-white/10 bg-white/5 p-7 md:col-span-2"
          >
            <div className="h-12 w-12 rounded-xl bg-indigo-500/20 text-indigo-300 flex items-center justify-center shrink-0">
              <MapPin className="h-6 w-6" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{c.addressLabel}</div>
              <div className="mt-1.5 text-xl font-semibold">{COMPANY.address}</div>
            </div>
          </motion.div>
        </div>

        <motion.div {...fadeUp} className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-7">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{c.detailsLabel}</div>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-y-4 gap-x-6">
            <div>
              <div className="text-sm text-slate-500">Firma</div>
              <div className="mt-1 font-medium text-slate-200">{COMPANY.name}</div>
            </div>
            <div>
              <div className="text-sm text-slate-500">KRS</div>
              <div className="mt-1 font-medium text-slate-200">{COMPANY.krs}</div>
            </div>
            <div>
              <div className="text-sm text-slate-500">NIP</div>
              <div className="mt-1 font-medium text-slate-200">{COMPANY.nip}</div>
            </div>
            <div>
              <div className="text-sm text-slate-500">REGON</div>
              <div className="mt-1 font-medium text-slate-200">{COMPANY.regon}</div>
            </div>
          </div>
        </motion.div>

        <div className="mt-20 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="MCS" className="h-10 w-auto object-contain brightness-0 invert opacity-90" />
          </div>
          <div className="text-center md:text-right">
            <div className="text-sm text-slate-400">{t.footer.tagline}</div>
            <div className="mt-1 text-sm text-slate-500">
              © {new Date().getFullYear()} {COMPANY.shortName} · {COMPANY.domain} · {t.footer.rights}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
